// Sentry initialization must be imported first!
import './instrument';

import React from 'react';
import ReactDOM from 'react-dom/client';
// Mantine Core styles must be imported first
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css'; // if using Mantine date components
import '@mantine/notifications/styles.css'; // if using Mantine notifications

import { MantineProvider, createTheme, MantineColorsTuple, MantineTheme } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { BrowserRouter } from 'react-router-dom';

import App from './App';
import './index.css';
import { ErrorBoundary } from './components/ErrorBoundary';


// Define custom colors based on logo
const gold: MantineColorsTuple = [
  '#fff8dc',
  '#fff2a6',
  '#ffeb70',
  '#ffe53a',
  '#ffd700',
  '#e6c200',
  '#ccad00',
  '#b39900',
  '#998400',
  '#806f00'
];

const pink: MantineColorsTuple = [
  '#fce4ec',
  '#f8bbd0',
  '#f491b4',
  '#f06498',
  '#ec407f',
  '#e91e6c',
  '#e61462', // Main Pink - Approximation from logo
  '#cc0a53',
  '#b60246',
  '#9f0037'
];

// Custom theme for Lykke Terapien
const lykkeTheme = createTheme({
  primaryColor: 'gold', // Use the new gold as primary
  colors: {
    gold, // Register the custom color
    pink, // Register the custom color
    // Keep the old teal for potential secondary use or remove if not needed
    teal: [
      '#e6f7f4', 
      '#c8ebe5',
      '#a7ded5',
      '#87d1c6',
      '#65c3b6',
      '#43b6a6',
      '#33a895', 
      '#238a7a',
      '#146c5f',
      '#074d44'
    ],
    // Define a dark color for the header background
    dark: [
      '#C1C2C5', 
      '#A6A7AB', 
      '#909296', 
      '#5c5f66', 
      '#373A40', 
      '#2C2E33', 
      '#25262b', // Use this for dark backgrounds? index 6
      '#1A1B1E', // Use this for header? index 7
      '#141517', 
      '#101113' 
    ],
  },
  fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
  headings: { 
    fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
    fontWeight: '600',
  },
  components: {
    Paper: {
      defaultProps: {
        p: 'lg',
        shadow: 'xs',
        withBorder: true, // Ensure borders are consistent
      },
    },
    Button: {
      defaultProps: {
        color: 'gold', // Default buttons to gold
        radius: 'sm',
      },
      // Style variants if needed (e.g., subtle on dark background)
    },
    AppShell: { // Style AppShell directly if possible
       styles: (theme: MantineTheme) => ({
           header: {
               backgroundColor: theme.colors.dark[7], // Dark header (#1A1B1E)
               borderBottom: `1px solid ${theme.colors.dark[5]}`, // Darker border
           },
           main: {
               // Set light background directly, dark mode will use theme defaults if enabled
               backgroundColor: '#FFFDF5', // Light cream background
           },
       }),
    },
  },
  other: {
    borderRadius: 8,
    transitionDuration: 200,
  },
  // defaultColorScheme: 'dark' // Can uncomment to force dark mode
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/* Default to light scheme, but theme includes dark variations */}
    <MantineProvider theme={lykkeTheme} defaultColorScheme="light">
      <Notifications position="top-right" />
      <BrowserRouter>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </BrowserRouter>
    </MantineProvider>
  </React.StrictMode>
);
